
import { WEAR_SLOTS, type BagSlotModel } from "./bag";
import { elem, installChrome, makeDraggable, styleInstaller } from "./chrome";
import { itemArtNode } from "./itemart";
import { ItemTooltip, attachHoverTooltip, slotTooltipData } from "./tooltip";

export interface EquipmentView {
  readonly entityId: number;
  readonly name: string;
  readonly worn: readonly BagSlotModel[];
}

const STYLE_ID = "equipment-view-style";
const STYLE = `
.equipment-view {
  /* uiequipmentdialog.py:24 loads this dialog's own board from
     "UIScript/EquipmentDialog.py". A sparse checkout of client/bin/pack/
     (only root/ and locale_en/) omits that script entirely, but a full
     checkout ships it under uiscript/uiscript/equipmentdialog.py -- and it
     turns out this build's 180x230 was never a guess: the real window
     declares exactly "width":180, "height":230 (equipmentdialog.py:10-11).
     It is also "style":("movable","float") (equipmentdialog.py:5) -- a
     dialog the player can drag around, unlike this fixed, screen-centred
     panel. That affordance gap is real and left open (see this file's own
     module docstring); it does not change the board's shape below. */
  position: fixed;
  z-index: 7;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 180px;
  height: 230px;
  padding: 0;
  color: #e8dcc0;
}
.equipment-view-head {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 28px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 8px;
}
.equipment-view-title { flex:1; color:#ffe9a8; font-weight:600; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.equipment-view-close { border:0; background:transparent; color:#c7ccd4; font:inherit; cursor:pointer; }
.equipment-view-grid {
  /* Sized and offset to match this dialog's own EquipmentSlot,
     equipmentdialog.py:45-52: a 150x182 table at (3,3) inside
     EquipmentBaseImage. */
  position: absolute;
  left: 15px;
  top: 33px;
  width: 150px;
  height: 182px;
  background: url("/ui/textures/equipment_bg_without_ring.png") 0 0 / 155px 187px no-repeat;
  image-rendering: pixelated;
}
.equipment-view-slot {
  position: absolute;
  width: 32px;
  height: 32px;
  min-height: 0;
  border: 0;
  background: transparent;
  padding: 0;
  color:inherit;
  text-align:left;
}
/* WEAR_SLOTS (bag.ts) walks the server's WearSlot wire order --
   WEAPON, BODY, HEAD, SHIELD, WRIST, FOOTS, NECK, EAR, UNIQUE1, UNIQUE2, BELT
   (server/equipment.py:151-165) -- which is *not* this dialog's own script's
   index order (BODY, HEAD, FOOTS, WRIST, WEAPON, NECK, EAR, UNIQUE1,
   UNIQUE2, ARROW, SHIELD; equipmentdialog.py:55-65). Each nth-child below
   is keyed to the DOM's wire-order position but carries the source's pixel
   position for that *role*, not for that list index -- getting the two
   orders crossed is what silently swapped Weapon and Body here before.
   There is no cell for ARROW: this build has no arrow WearSlot
   (server/equipment.py:110-117, whose own docstring calls the omission out
   by name), and the source's own (114,2) cell for it is simply unused here
   rather than reassigned to something else.

   These eleven positions are equipmentdialog.py's own numbers, and the
   claim that used to stand here -- that the two boards "agree on six of
   eleven cells but hand-place Neck, Ear, Unique1 and Unique2 differently" --
   is only true of the *base* pack's uiscript/inventorywindow.py:64-81. The
   board InventoryWindow actually loads is the localised one
   (uiinventory.py:292-293, taken because ITEM_MALL_BUTTON_ENABLE is True at
   :22), and locale/en/ui/inventorywindow.py:74-95 matches this dialog's
   tuple cell for cell on all eleven, belt at (39,106) included. So the two
   windows a player sees agree; the disagreement is with a third, unloaded
   script in the same tree. equipmentdialog.py remains the right citation for
   *this* window (uiequipmentdialog.py's own "UIScript/EquipmentDialog.py"). */
.equipment-view-slot:nth-child(1) { left:3px; top:3px; height:96px; }   /* Weapon: equipmentdialog.py:59 */
.equipment-view-slot:nth-child(2) { left:39px; top:37px; height:64px; } /* Body: equipmentdialog.py:55 */
.equipment-view-slot:nth-child(3) { left:39px; top:2px; }               /* Head: equipmentdialog.py:56 */
.equipment-view-slot:nth-child(4) { left:75px; top:35px; }              /* Shield: equipmentdialog.py:65 */
.equipment-view-slot:nth-child(5) { left:75px; top:67px; }              /* Wrist: equipmentdialog.py:58 */
.equipment-view-slot:nth-child(6) { left:39px; top:145px; }             /* Foots: equipmentdialog.py:57 */
.equipment-view-slot:nth-child(7) { left:114px; top:67px; }             /* Neck: equipmentdialog.py:60 */
.equipment-view-slot:nth-child(8) { left:114px; top:35px; }             /* Ear: equipmentdialog.py:61 */
.equipment-view-slot:nth-child(9) { left:2px; top:145px; }              /* Unique1: equipmentdialog.py:62 */
.equipment-view-slot:nth-child(10) { left:75px; top:145px; }            /* Unique2: equipmentdialog.py:63 */
/* Live in this script the way the two ring cells right above it are not --
   "index":21/22 (Ring1/Ring2) stay commented out at equipmentdialog.py:66-69
   ("반지 슬롯1/2", ring slot 1/2), never shipped even in a full checkout,
   while "index":23 (belt, "벨트") is a real, uncommented entry at (39,106)
   (equipmentdialog.py:70-71). That is independent, client-side confirmation
   of the same fact server/equipment.py's WearSlot.BELT docstring already
   gives from the wire side: belt is real, both rings are not (no ring rows
   exist in gamedata.json either, per parity.json's own inventory row).

   Costume body/hair (WearSlot.COSTUME_BODY 19 / COSTUME_HAIR 20, matching
   length.h:125-126) are live on the wire and equip through Equip/Unequip,
   but they are not drawn here: equipmentdialog.py's slot tuple never
   names them. The original puts those two cells on the separate CostumeWindow
   (costumewindow.py:73-76, gated by app.ENABLE_COSTUME_SYSTEM). Extra
   cells are drawn only when the original layout has them and the wear
   index exists on the wire -- here only the first half is missing. */
.equipment-view-slot:nth-child(11) { left:39px; top:106px; }            /* Belt: equipmentdialog.py:71 */
.equipment-view-slot:nth-child(12) { left:114px; top:2px; }             /* Arrow hole: equipmentdialog.py:64, no WearSlot */
.equipment-view-slot[data-arrow-hole="true"] { pointer-events: none; }
.equipment-view-slot[data-filled="true"] { border-color:rgba(255,215,130,.35); }
.equipment-view-label, .equipment-view-name { display:none; }
.equipment-view-icon { width:32px; height:100%; display:flex; align-items:flex-start; justify-content:center; color:#68727e; }
.equipment-view-icon .item-icon { height:94%; max-width:100%; }
`;

const installStyle = styleInstaller(STYLE_ID, STYLE);

export class EquipmentPanel {
  private readonly root: HTMLElement;
  private readonly title: HTMLElement;
  private readonly grid: HTMLElement;
  private readonly tooltip: ItemTooltip;
  private openOnUpdate = false;

  constructor(container: HTMLElement) {
    const doc = container.ownerDocument;
    installChrome(doc);
    installStyle(doc);
    this.tooltip = new ItemTooltip(doc);
    this.root = elem(doc, "section", "equipment-view m2-board");
    this.root.hidden = true;
    const head = elem(doc, "div", "equipment-view-head");
    this.title = elem(doc, "div", "m2-titlebar equipment-view-title");
    const close = doc.createElement("button");
    close.type = "button";
    close.className = "m2-close";
    close.setAttribute("aria-label", "Close");
    close.addEventListener("click", () => this.close());
    head.append(this.title, close);
    makeDraggable(this.root, this.title);
    this.grid = elem(doc, "div", "equipment-view-grid");
    this.root.append(head, this.grid);
    container.append(this.root);
    // `EquipmentDialog.OnPressEscapeKey` closes the legacy surface
    // (`uiequipmentdialog.py:122-124`).  Keep the browser equivalent scoped to
    // a visible inspection so Escape never intercepts normal play.
    doc.defaultView?.addEventListener("keydown", (event) => {
      if (event.key !== "Escape" || !this.visible) return;
      event.preventDefault();
      event.stopPropagation();
      this.close();
    });
  }

  get visible(): boolean { return !this.root.hidden; }

  update(view: EquipmentView): void {
    const wasVisible = this.visible;
    if (view.entityId === 0) {
      this.openOnUpdate = false;
      this.close();
      return;
    }
    const bySlot = new Map(view.worn.map((slot) => [slot.slot, slot]));
    this.title.textContent = `${view.name === "" ? `Player #${view.entityId}` : view.name}'s equipment`;
    this.grid.replaceChildren(...WEAR_SLOTS.map(({ wear, label }) => {
      const slot = bySlot.get(wear);
      const cell = elem(this.root.ownerDocument, "div", "equipment-view-slot");
      cell.dataset.filled = String(slot !== undefined);
      const slotLabel = elem(this.root.ownerDocument, "span", "equipment-view-label", label);
      const icon = elem(this.root.ownerDocument, "span", "equipment-view-icon");
      const name = elem(this.root.ownerDocument, "span", "equipment-view-name");
      if (slot !== undefined) {
        icon.append(itemArtNode(slot.vnum, slot.kind, this.root.ownerDocument));
        name.textContent = slot.count > 1 ? `${slot.name} x${slot.count}` : slot.name;
        attachHoverTooltip(cell, this.tooltip, () => slotTooltipData(slot));
      } else {
        name.textContent = "Empty";
      }
      cell.append(slotLabel, icon, name);
      return cell;
    }), (() => {
      const hole = elem(this.root.ownerDocument, "div", "equipment-view-slot");
      hole.dataset.arrowHole = "true";
      hole.setAttribute("aria-hidden", "true");
      return hole;
    })());
    this.root.hidden = !(wasVisible || this.openOnUpdate);
    this.openOnUpdate = false;
  }

  /** Ask the next server-confirmed refresh to open the panel. */
  requestOpen(): void {
    this.openOnUpdate = true;
  }

  close(): void {
    this.tooltip.hide();
    this.root.hidden = true;
  }
}
